import { useState, useEffect, useCallback } from 'react';
import { 
  getAllOrganizations, 
  getOrganizationDetails, 
  updateOrganizationAdmin, 
  deleteOrganizationAdmin,
  getAllUsers,
  resetUserPassword,
  toggleUserActivation,
  getPlatformAnalytics,
  getTierConfigurations,
  updateTierConfiguration,
  getSMTPConfig,
  createSMTPConfig,
  updateSMTPConfig,
  deleteSMTPConfig,
  testSMTPConfig,
  getErrorMessage
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Shield, 
  Building2, 
  Users, 
  CreditCard, 
  Settings,
  BarChart3,
  Search,
  Eye,
  Trash2,
  Key,
  UserX,
  UserCheck,
  Edit,
  Mail,
  Send,
  Server,
  Loader2
} from 'lucide-react';

export const SuperAdminPage = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [analytics, setAnalytics] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [orgDetailsDialog, setOrgDetailsDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetails, setOrgDetails] = useState(null);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editTierDialog, setEditTierDialog] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierFormData, setTierFormData] = useState({});
  
  // SMTP Configuration states
  const [smtpConfig, setSmtpConfig] = useState(null);
  const [smtpFormData, setSmtpFormData] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    use_tls: true,
    use_ssl: false,
    from_email: '',
    from_name: 'TeamTime'
  });
  const [testEmail, setTestEmail] = useState('');
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, orgsRes, usersRes, tiersRes, smtpRes] = await Promise.all([
        getPlatformAnalytics(),
        getAllOrganizations(),
        getAllUsers(),
        getTierConfigurations(),
        getSMTPConfig().catch(() => ({ data: null }))
      ]);
      setAnalytics(analyticsRes.data);
      setOrganizations(orgsRes.data);
      setUsers(usersRes.data);
      setTiers(tiersRes.data);
      if (smtpRes.data && Object.keys(smtpRes.data).length > 0) {
        setSmtpConfig(smtpRes.data);
        setSmtpFormData({
          host: smtpRes.data.host || '',
          port: smtpRes.data.port || 587,
          username: smtpRes.data.username || '',
          password: '',
          use_tls: smtpRes.data.use_tls ?? true,
          use_ssl: smtpRes.data.use_ssl ?? false,
          from_email: smtpRes.data.from_email || '',
          from_name: smtpRes.data.from_name || 'TeamTime'
        });
      }
    } catch (error) {
      console.error('Failed to fetch super admin data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewOrgDetails = async (org) => {
    setSelectedOrg(org);
    try {
      const response = await getOrganizationDetails(org.id);
      setOrgDetails(response.data);
      setOrgDetailsDialog(true);
    } catch (error) {
      toast.error('Failed to load organization details');
    }
  };

  const handleUpdateOrgTier = async (orgId, newTier) => {
    try {
      const tierConfig = tiers.find(t => t.tier === newTier);
      await updateOrganizationAdmin(orgId, {
        subscription_tier: newTier,
        max_employees: tierConfig?.max_employees || 5,
        max_departments: tierConfig?.max_departments || 2,
        max_branches: tierConfig?.max_branches || 1,
        features: tierConfig?.features || {}
      });
      toast.success('Organization tier updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update tier');
    }
  };

  const handleDeleteOrg = async (orgId) => {
    if (!window.confirm('Are you sure? This will delete all organization data permanently.')) return;
    try {
      await deleteOrganizationAdmin(orgId);
      toast.success('Organization deleted');
      setOrgDetailsDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete organization');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await resetUserPassword(selectedUser.id, newPassword);
      toast.success('Password reset successfully');
      setResetPasswordDialog(false);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleToggleUserActive = async (user) => {
    try {
      await toggleUserActivation(user.id, !user.is_active);
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleEditTier = (tier) => {
    setEditingTier(tier);
    setTierFormData({
      max_employees: tier.max_employees,
      max_departments: tier.max_departments,
      max_branches: tier.max_branches,
      price: tier.price,
      features: tier.features || {}
    });
    setEditTierDialog(true);
  };

  const handleSaveTier = async () => {
    try {
      await updateTierConfiguration(editingTier.tier, {
        tier: editingTier.tier,
        ...tierFormData
      });
      toast.success('Tier configuration updated');
      setEditTierDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update tier');
    }
  };

  // SMTP Configuration Handlers
  const handleSaveSMTP = async () => {
    setSmtpSaving(true);
    try {
      if (smtpConfig) {
        await updateSMTPConfig(smtpFormData);
      } else {
        await createSMTPConfig(smtpFormData);
      }
      toast.success('SMTP configuration saved successfully');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save SMTP configuration'));
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleTestSMTP = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    setSmtpTesting(true);
    try {
      await testSMTPConfig(testEmail);
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send test email'));
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleDeleteSMTP = async () => {
    if (!window.confirm('Are you sure you want to delete the SMTP configuration? Email notifications will be disabled.')) {
      return;
    }
    try {
      await deleteSMTPConfig();
      setSmtpConfig(null);
      setSmtpFormData({
        host: '',
        port: 587,
        username: '',
        password: '',
        use_tls: true,
        use_ssl: false,
        from_email: '',
        from_name: 'TeamTime'
      });
      toast.success('SMTP configuration deleted');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete SMTP configuration'));
    }
  };

  const filteredOrgs = organizations.filter(org => 
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTierBadge = (tier) => {
    const styles = {
      free: 'bg-slate-100 text-slate-700',
      pro: 'bg-blue-100 text-blue-700',
      enterprise: 'bg-violet-100 text-violet-700'
    };
    return <Badge className={styles[tier] || styles.free}>{tier?.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="super-admin-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="super-admin-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
          <Shield className="w-6 h-6 text-violet-600" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">
            Super Admin Dashboard
          </h1>
          <p className="text-muted-foreground">
            Manage all organizations, users, and platform settings
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="analytics" className="gap-2" data-testid="tab-analytics">
            <BarChart3 className="w-4 h-4" /> Analytics
          </TabsTrigger>
          <TabsTrigger value="organizations" className="gap-2" data-testid="tab-organizations">
            <Building2 className="w-4 h-4" /> Orgs
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2" data-testid="tab-users">
            <Users className="w-4 h-4" /> Users
          </TabsTrigger>
          <TabsTrigger value="tiers" className="gap-2" data-testid="tab-tiers">
            <CreditCard className="w-4 h-4" /> Tiers
          </TabsTrigger>
          <TabsTrigger value="smtp" className="gap-2" data-testid="tab-smtp">
            <Mail className="w-4 h-4" /> Email
          </TabsTrigger>
        </TabsList>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Organizations</p>
                    <p className="text-3xl font-bold">{analytics?.total_organizations || 0}</p>
                  </div>
                  <Building2 className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                    <p className="text-3xl font-bold">{analytics?.total_users || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Users</p>
                    <p className="text-3xl font-bold">{analytics?.active_users || 0}</p>
                  </div>
                  <UserCheck className="w-8 h-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Today&apos;s Clock-ins</p>
                    <p className="text-3xl font-bold">{analytics?.today_clock_ins || 0}</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Subscription Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-slate-50 text-center">
                  <p className="text-3xl font-bold">{analytics?.subscriptions?.free || 0}</p>
                  <p className="text-sm text-muted-foreground">Free</p>
                </div>
                <div className="p-4 rounded-xl bg-blue-50 text-center">
                  <p className="text-3xl font-bold text-blue-600">{analytics?.subscriptions?.pro || 0}</p>
                  <p className="text-sm text-muted-foreground">Pro</p>
                </div>
                <div className="p-4 rounded-xl bg-violet-50 text-center">
                  <p className="text-3xl font-bold text-violet-600">{analytics?.subscriptions?.enterprise || 0}</p>
                  <p className="text-sm text-muted-foreground">Enterprise</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Organizations Tab */}
        <TabsContent value="organizations" className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search organizations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-organizations"
              />
            </div>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Employees</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrgs.map((org) => (
                  <TableRow key={org.id} data-testid={`org-row-${org.id}`}>
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell>{getTierBadge(org.subscription_tier)}</TableCell>
                    <TableCell>{org.employee_count} / {org.max_employees === -1 ? '∞' : org.max_employees}</TableCell>
                    <TableCell>
                      <Badge className={org.subscription_status === 'active' ? 'badge-success' : 'badge-warning'}>
                        {org.subscription_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewOrgDetails(org)}
                          data-testid={`view-org-${org.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Select onValueChange={(value) => handleUpdateOrgTier(org.id, value)}>
                          <SelectTrigger className="w-24 h-8">
                            <SelectValue placeholder="Tier" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="enterprise">Enterprise</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-users"
              />
            </div>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.slice(0, 50).map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">{user.first_name} {user.last_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.org_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{user.role}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={user.is_active ? 'badge-success' : 'badge-error'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedUser(user);
                            setResetPasswordDialog(true);
                          }}
                          title="Reset Password"
                          data-testid={`reset-password-${user.id}`}
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleUserActive(user)}
                          title={user.is_active ? 'Deactivate' : 'Activate'}
                          data-testid={`toggle-user-${user.id}`}
                        >
                          {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Tiers Tab */}
        <TabsContent value="tiers" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((tier) => (
              <Card key={tier.tier} className="relative" data-testid={`tier-card-${tier.tier}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="capitalize">{tier.tier || tier.name}</CardTitle>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditTier(tier)}
                      data-testid={`edit-tier-${tier.tier}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                  <CardDescription>
                    ${tier.price?.toFixed(2) || '0.00'}/month
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Employees</span>
                      <span className="font-mono">{tier.max_employees === -1 ? 'Unlimited' : tier.max_employees}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Departments</span>
                      <span className="font-mono">{tier.max_departments === -1 ? 'Unlimited' : tier.max_departments}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Branches</span>
                      <span className="font-mono">{tier.max_branches === -1 ? 'Unlimited' : tier.max_branches}</span>
                    </div>
                  </div>
                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium mb-2">Features</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(tier.features || {}).map(([feature, enabled]) => (
                        <Badge
                          key={feature}
                          variant="outline"
                          className={enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-400'}
                        >
                          {feature.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* SMTP Configuration Tab */}
        <TabsContent value="smtp" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* SMTP Settings Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="w-5 h-5" />
                  SMTP Server Configuration
                </CardTitle>
                <CardDescription>
                  Configure email server settings for sending notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp-host">SMTP Host</Label>
                    <Input
                      id="smtp-host"
                      placeholder="smtp.gmail.com"
                      value={smtpFormData.host}
                      onChange={(e) => setSmtpFormData(prev => ({ ...prev, host: e.target.value }))}
                      data-testid="smtp-host"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-port">Port</Label>
                    <Input
                      id="smtp-port"
                      type="number"
                      placeholder="587"
                      value={smtpFormData.port}
                      onChange={(e) => setSmtpFormData(prev => ({ ...prev, port: parseInt(e.target.value) || 587 }))}
                      data-testid="smtp-port"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp-username">Username</Label>
                    <Input
                      id="smtp-username"
                      placeholder="your-email@gmail.com"
                      value={smtpFormData.username}
                      onChange={(e) => setSmtpFormData(prev => ({ ...prev, username: e.target.value }))}
                      data-testid="smtp-username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-password">Password</Label>
                    <Input
                      id="smtp-password"
                      type="password"
                      placeholder={smtpConfig ? "••••••••" : "Enter password"}
                      value={smtpFormData.password}
                      onChange={(e) => setSmtpFormData(prev => ({ ...prev, password: e.target.value }))}
                      data-testid="smtp-password"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="from-email">From Email</Label>
                    <Input
                      id="from-email"
                      type="email"
                      placeholder="noreply@yourdomain.com"
                      value={smtpFormData.from_email}
                      onChange={(e) => setSmtpFormData(prev => ({ ...prev, from_email: e.target.value }))}
                      data-testid="smtp-from-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="from-name">From Name</Label>
                    <Input
                      id="from-name"
                      placeholder="TeamTime"
                      value={smtpFormData.from_name}
                      onChange={(e) => setSmtpFormData(prev => ({ ...prev, from_name: e.target.value }))}
                      data-testid="smtp-from-name"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="use-tls"
                      checked={smtpFormData.use_tls}
                      onCheckedChange={(checked) => setSmtpFormData(prev => ({ 
                        ...prev, 
                        use_tls: checked,
                        use_ssl: checked ? false : prev.use_ssl 
                      }))}
                      data-testid="smtp-use-tls"
                    />
                    <Label htmlFor="use-tls">Use TLS (Port 587)</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="use-ssl"
                      checked={smtpFormData.use_ssl}
                      onCheckedChange={(checked) => setSmtpFormData(prev => ({ 
                        ...prev, 
                        use_ssl: checked,
                        use_tls: checked ? false : prev.use_tls 
                      }))}
                      data-testid="smtp-use-ssl"
                    />
                    <Label htmlFor="use-ssl">Use SSL (Port 465)</Label>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button 
                    onClick={handleSaveSMTP} 
                    disabled={smtpSaving}
                    className="flex-1"
                    data-testid="save-smtp-btn"
                  >
                    {smtpSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Configuration'
                    )}
                  </Button>
                  {smtpConfig && (
                    <Button 
                      variant="destructive" 
                      onClick={handleDeleteSMTP}
                      data-testid="delete-smtp-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Test Email Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  Test Configuration
                </CardTitle>
                <CardDescription>
                  Send a test email to verify your SMTP settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {smtpConfig ? (
                  <>
                    <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                      <p className="text-sm text-green-700 font-medium">
                        ✓ SMTP Configured
                      </p>
                      <p className="text-sm text-green-600 mt-1">
                        Server: {smtpConfig.host}:{smtpConfig.port}
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="test-email">Test Email Address</Label>
                      <Input
                        id="test-email"
                        type="email"
                        placeholder="test@example.com"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        data-testid="test-email-input"
                      />
                    </div>

                    <Button 
                      onClick={handleTestSMTP} 
                      disabled={smtpTesting || !testEmail}
                      className="w-full"
                      variant="outline"
                      data-testid="send-test-email-btn"
                    >
                      {smtpTesting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Test Email
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-sm text-amber-700 font-medium">
                      ⚠ No SMTP Configuration
                    </p>
                    <p className="text-sm text-amber-600 mt-1">
                      Configure your SMTP server settings first to enable email notifications.
                    </p>
                  </div>
                )}

                <div className="pt-4 border-t space-y-3">
                  <h4 className="text-sm font-medium">Common SMTP Providers</h4>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p><strong>Gmail:</strong> smtp.gmail.com, Port 587 (TLS)</p>
                    <p><strong>Outlook:</strong> smtp.office365.com, Port 587 (TLS)</p>
                    <p><strong>Yahoo:</strong> smtp.mail.yahoo.com, Port 587 (TLS)</p>
                    <p><strong>SendGrid:</strong> smtp.sendgrid.net, Port 587 (TLS)</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Note: For Gmail, use an App Password instead of your regular password.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Organization Details Dialog */}
      <Dialog open={orgDetailsDialog} onOpenChange={setOrgDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedOrg?.name}</DialogTitle>
            <DialogDescription>Organization details and management</DialogDescription>
          </DialogHeader>
          {orgDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Subscription</p>
                  <p className="font-medium capitalize">{orgDetails.organization?.subscription_tier}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Employees</p>
                  <p className="font-medium">{orgDetails.stats?.employees}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Departments</p>
                  <p className="font-medium">{orgDetails.stats?.departments}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Time Entries</p>
                  <p className="font-medium">{orgDetails.stats?.time_entries}</p>
                </div>
              </div>
              <div className="pt-4 flex justify-end gap-2">
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteOrg(selectedOrg.id)}
                  data-testid="delete-org-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Organization
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog} onOpenChange={setResetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Reset password for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                data-testid="new-password-input"
              />
            </div>
            <Button onClick={handleResetPassword} className="w-full" data-testid="confirm-reset-btn">
              Reset Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Tier Dialog */}
      <Dialog open={editTierDialog} onOpenChange={setEditTierDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {editingTier?.tier} Tier</DialogTitle>
            <DialogDescription>Configure tier limits and features</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Employees</Label>
                <Input
                  type="number"
                  value={tierFormData.max_employees}
                  onChange={(e) => setTierFormData(prev => ({ ...prev, max_employees: parseInt(e.target.value) }))}
                  data-testid="tier-max-employees"
                />
              </div>
              <div className="space-y-2">
                <Label>Max Departments</Label>
                <Input
                  type="number"
                  value={tierFormData.max_departments}
                  onChange={(e) => setTierFormData(prev => ({ ...prev, max_departments: parseInt(e.target.value) }))}
                  data-testid="tier-max-departments"
                />
              </div>
              <div className="space-y-2">
                <Label>Max Branches</Label>
                <Input
                  type="number"
                  value={tierFormData.max_branches}
                  onChange={(e) => setTierFormData(prev => ({ ...prev, max_branches: parseInt(e.target.value) }))}
                  data-testid="tier-max-branches"
                />
              </div>
              <div className="space-y-2">
                <Label>Price ($/month)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={tierFormData.price}
                  onChange={(e) => setTierFormData(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                  data-testid="tier-price"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Features</Label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(tierFormData.features || {}).map(([feature, enabled]) => (
                  <div key={feature} className="flex items-center justify-between p-2 rounded-lg bg-muted">
                    <span className="text-sm capitalize">{feature.replace(/_/g, ' ')}</span>
                    <Switch
                      checked={enabled}
                      onCheckedChange={(checked) => setTierFormData(prev => ({
                        ...prev,
                        features: { ...prev.features, [feature]: checked }
                      }))}
                      data-testid={`tier-feature-${feature}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={handleSaveTier} className="w-full" data-testid="save-tier-btn">
              Save Configuration
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
